import type { IntlShape } from 'react-intl'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_CRUISE, OFFER_TYPE_VILLA } from 'constants/offer'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import { isolatePhoneNumber } from 'lib/format/formatPhoneNumber'

export function excludeNullOrUndefined<T>(item: T | undefined | null | false): item is T {
  return Boolean(item)
}

function getAccommodationDesignationLabels() {
  const intl = getCurrentIntl()

  return {
    accommodation: intl.formatMessage({
      id: 'zdmuwpJMsikmxWcwof6n',
      defaultMessage: 'accommodation',
      description: 'Generic accommodation designation label used in checkout data and rendered as a heading in review sections',
    }),
    preAccommodation: intl.formatMessage({
      id: 'NnZL1OXra6V4ITrsVqOF',
      defaultMessage: 'pre accommodation',
      description: 'Checkout designation label for accommodation booked before a tour or cruise and rendered in review sections',
    }),
    postAccommodation: intl.formatMessage({
      id: 's2dGUEKVliNUQz3K9hQn',
      defaultMessage: 'post accommodation',
      description: 'Checkout designation label for accommodation booked after a tour or cruise and rendered in review sections',
    }),
  }
}

export function getDefaultAccommodationDesignation(): string {
  return getAccommodationDesignationLabels().accommodation
}

export function getAccommodationDesignationWithTourBundle(
  startDate: string,
  itemBundleId?: string,
  tourV2Items?: Array<App.Checkout.TourV2Item>,
): string {
  const {
    accommodation,
    postAccommodation,
    preAccommodation,
  } = getAccommodationDesignationLabels()

  const defaultDesignation = accommodation
  if (!itemBundleId || !tourV2Items?.length) {
    return defaultDesignation
  }
  const tourV2Item = tourV2Items.find(item => item.itemId === itemBundleId)
  if (!tourV2Item) {
    return defaultDesignation
  }
  return new Date(tourV2Item.startDate) > new Date(startDate) ? preAccommodation : postAccommodation
}

export function getAccommodationDesignationWithCruise(
  checkIn?: string,
  checkOut?: string,
  cruiseItems?: Array<App.Checkout.CruiseItem>,
): string {
  const {
    accommodation,
    postAccommodation,
    preAccommodation,
  } = getAccommodationDesignationLabels()

  const defaultDesignation = accommodation
  if (!checkIn || !checkOut || !cruiseItems?.length) {
    return defaultDesignation
  }

  // Check if hotel checkout matches cruise start (pre-cruise)
  const isPreCruise = cruiseItems.some(item => item.startDate === checkOut)
  if (isPreCruise) {
    return preAccommodation
  }

  // Check if hotel checkin matches cruise end (post-cruise)
  const isPostCruise = cruiseItems.some(item => item.endDate === checkIn)
  if (isPostCruise) {
    return postAccommodation
  }

  return defaultDesignation
}

export function getBreakdownTitle(offer: App.Checkout.AccommodationOfferView, intl: IntlShape): string {
  switch (offer.offerType) {
    case OFFER_TYPE_HOTEL:
    case OFFER_TYPE_BED_BANK:
    case OFFER_TYPE_ALWAYS_ON:
    case OFFER_TYPE_VILLA:
      return intl.formatMessage({
        id: '5YTw3yvipPddoj1taqU6',
        defaultMessage: 'Accommodation',
        description: 'Checkout breakdown title for hotel and accommodation offers',
      })
    case OFFER_TYPE_TOUR:
      return intl.formatMessage(productLabels.cruise)
    case OFFER_TYPE_TOUR_V2:
      return intl.formatMessage(productLabels.tour)
    case OFFER_TYPE_CRUISE:
      return intl.formatMessage(productLabels.cruise)
    default:
      return intl.formatMessage({
        id: 'tnkE1tpiUk0lj-AQWi7j',
        defaultMessage: 'Package',
        description: 'Checkout breakdown title for generic package offers',
      })
  }
}

// Added extracted this from AccountTakeOverModal.tsx as needed values elsewhere - maybe can be updated later to use lib-regions flow?
export function getTollFreeSalesContactNumber(region: string, currentContact: App.GeoContactsTypes): App.GeoContacts {
  switch (region) {
    case 'AU':
      return {
        local: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        international: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        default: 'local',
        defaultContact: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        autoVerification: false,
      }
    case 'NZ':
      return {
        local: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        international: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        default: 'local',
        defaultContact: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        autoVerification: false,
      }
    case 'US':
      return {
        local: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        international: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        default: 'local',
        defaultContact: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        autoVerification: false,
      }
    case 'GB':
      return {
        local: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        international: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        default: 'local',
        defaultContact: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        autoVerification: false,
      }
    default:
      return currentContact.highValue || currentContact.general
  }
}

export function isFlightWithEmbeddedInsurance(flight: App.Checkout.FlightItemFlight | App.Checkout.FlightItemViewJourney): boolean {
  return Boolean(flight.fareFamily?.withEmbeddedInsurance || flight.withEmbeddedInsurance)
}
